import { Stack, Typography, Box, Divider, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import DashboardWidgetCard from '../DashboardWidgetCard';
import useWidgetData from '../useWidgetData';
import { formatWidgetValue } from '../format';

// Renders a daily-statistic widget from the normalized backend payload
// ({ primary, primaryLabel, primaryFormat, stats: [{ label, value, format }] }).
// Data is served from the shared backend cache, so the same day + branch scope
// is computed once no matter how many clients are looking at it.
export default function StatWidget({ widget, editMode, onHide, dragHandleProps }) {
  const { payload, meta, loading, error, refresh } = useWidgetData(widget.id);

  const stats = Array.isArray(payload?.stats) ? payload.stats : [];

  const computedHint =
    meta?.computedAt && !loading
      ? `Stand ${dayjs(meta.computedAt).format('HH:mm')}${meta.branchScope && meta.branchScope !== 'all' ? '' : ' · alle Standorte'}`
      : null;

  return (
    <DashboardWidgetCard
      title={widget.title}
      icon={widget.icon}
      accent={widget.accent}
      link={widget.link}
      loading={loading}
      error={error}
      onRetry={() => refresh({ forceRecompute: false })}
      editMode={editMode}
      onHide={onHide}
      dragHandleProps={dragHandleProps}
      footer={
        computedHint ? (
          <Tooltip title='Tagesaktuell, im Hintergrund einmal pro Standort berechnet'>
            <Typography variant='caption' color='text.secondary'>
              {computedHint}
            </Typography>
          </Tooltip>
        ) : null
      }
    >
      <Stack spacing={0.25} sx={{ flexGrow: 1 }} justifyContent='center'>
        <Typography
          sx={{ fontWeight: 800, lineHeight: 1, fontSize: { xs: '1.9rem', sm: '2.2rem' }, color: `${widget.accent || 'primary'}.main` }}
        >
          {payload ? formatWidgetValue(payload.primary, payload.primaryFormat) : '—'}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 500 }}>
          {payload?.primaryLabel || widget.title}
        </Typography>

        {stats.length > 0 && (
          <Stack
            direction='row'
            spacing={1.5}
            divider={<Divider orientation='vertical' flexItem />}
            sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}
          >
            {stats.map(stat => (
              <Box key={stat.label} sx={{ minWidth: 56 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.1 }}>
                  {formatWidgetValue(stat.value, stat.format)}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </DashboardWidgetCard>
  );
}
